import {
    Button,
    DropZone,
    Form,
    FormLayout,
    InlineError,
    Link,
    Modal,
    TextStyle,
} from "@shopify/polaris";
import { arrInvalid, handleError, reducerFn } from "../../../helper";
import { useMutation } from "@apollo/react-hooks";
import { IMPORT_STORE_DEPOSIT_HISTORIES } from "../../../graphql/mutations";
import React from "react";
import styled from "styled-components";

export default function ImportDepositHistory({
    open,
    onClose,
    store,
    toggleActiveToast,
    setNotify,
    refetch,
}) {
    const { id } = store || {};

    const [state, setState] = React.useReducer(reducerFn, {
        openDialog: false,
        fileError: null,
        file: null,
    });

    const timeoutRef = React.useRef(null);
    const [importStoreDepositHistory, { loading }] = useMutation(
        IMPORT_STORE_DEPOSIT_HISTORIES,
        {
            onCompleted: () => {
                setNotify({
                    msg: "Import deposit history success",
                    err: false,
                });
                onClose && onClose();
                if (refetch) {
                    timeoutRef.current && clearTimeout(timeoutRef.current);
                    timeoutRef.current = setTimeout(() => {
                        refetch();
                    }, 1500);
                }
            },
            onError: (err) => {
                setNotify({ msg: handleError(err?.toString()), err: true });
            },
        },
    );

    const handleSubmit = React.useCallback(async () => {
        if (!state.file || !id) return;

        let values = await readFile(state.file);

        if (arrInvalid(values)) return;

        values = values.map((item) => ({ storeId: id, ...item }));

        toggleActiveToast && toggleActiveToast(true);
        setNotify && setNotify({ msg: null, err: false });

        importStoreDepositHistory({
            variables: {
                input: values,
            },
        });
    }, [
        state.file,
        id,
        toggleActiveToast,
        setNotify,
        importStoreDepositHistory,
    ]);

    const handleDrop = React.useCallback(
        async (_, acceptedFiles, rejectedFiles) => {
            setState({ file: null, fileError: null });

            const newState = {};
            if (rejectedFiles[0]) {
                newState.file = null;

                const name = rejectedFiles[0].name;
                newState.fileError = `${name}'s type invalid`;
            } else if (acceptedFiles[0]) {
                newState.file = acceptedFiles[0];
                newState.fileError = null;
            }

            setState(newState);
        },
        [],
    );

    const handleValidator = React.useCallback((file) => {
        if (!file || !file.type || file.type !== "text/csv") return false;
        return true;
    }, []);

    return (
        <Modal
            title="Import Deposit History"
            sectioned
            open={open}
            onClose={onClose}
        >
            <Form onSubmit={handleSubmit}>
                <FormLayout>
                    <DropZone
                        onClick={() => {
                            setState({ openDialog: true });
                        }}
                        onDrop={handleDrop}
                        accept={state.openDialog ? ".csv" : undefined}
                        openFileDialog={state.openDialog}
                        onFileDialogClose={() => {
                            setState({ openDialog: false });
                        }}
                        customValidator={handleValidator}
                        overlay
                        errorOverlayText={
                            "File type is invalid. Try again with .csv"
                        }
                        children={
                            <div
                                className="flex items-center justify-center w-full"
                                style={{ height: "100%" }}
                            >
                                <TextStyle>
                                    Click to select file Or drop file to import
                                </TextStyle>
                            </div>
                        }
                        allowMultiple={false}
                    />
                    {!!state.fileError && (
                        <InlineError message={state.fileError} />
                    )}

                    {!!state.file ? <p>{state.file?.name}</p> : null}

                    <p>Etsy CSV file</p>

                    <Button submit primary loading={loading} disabled={loading}>
                        Import
                    </Button>
                </FormLayout>
            </Form>
        </Modal>
    );
}

const HEADER_REGEX = {
    Date: /date/i,
    Amount: /amount/i,
    Currency: /currency/i,
    Status: /status/i,
    bankAccountEndingDigits: /bank_account_ending_digits/i,
};

function replaceHeader(str, sep = " ") {
    return str.replace(new RegExp(sep, "gi"), "_");
}

function matchFn(pt) {
    return (item) => !!replaceHeader(item).match(pt);
}

async function readFile(file) {
    return new Promise(async (res, rej) => {
        if (!file) rej("File is required");

        const reader = new FileReader();
        reader.onload = function (evt) {
            const result = evt.target.result;
            const decode = new TextDecoder("utf-8");
            const str = decode.decode(result);

            if (!str || typeof str !== "string") {
                rej("could not read data");
            }

            const [header, ...content] = str.split(/\n/);
            const headerSplitted = header.split(/\,/);

            const dateIndex = headerSplitted.findIndex(
                matchFn(HEADER_REGEX.Date),
            );

            const amountIndex = headerSplitted.findIndex(
                matchFn(HEADER_REGEX.Amount),
            );

            const currencyIndex = headerSplitted.findIndex(
                matchFn(HEADER_REGEX.Currency),
            );
            const bankIndex = headerSplitted.findIndex(
                matchFn(HEADER_REGEX.bankAccountEndingDigits),
            );

            const values = [];
            for (const line of content) {
                if (!line) continue;

                const item = line
                    .split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)
                    .map((i) => (i || "").trim());

                let date = item[dateIndex] || "";
                date = date.replace(/\"/gi, "");
                const value = {
                    date,
                    amount: Number.parseFloat(item[amountIndex] || 0),
                    currency: item[currencyIndex],
                    bankAccountEndingDigits: item[bankIndex],
                };

                values.push(value);
            }
            res(values);
        };

        reader.readAsArrayBuffer(file);
    });
}

const Wrapper = styled.div``;
